import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, ExternalLink } from "lucide-react";
import { optionOf } from "@/components/boardsModel";

// One cell of a board, per column type. Read state is what you see; a click
// turns it into its editor; leaving it saves. Every editor calls onChange with
// the wire value the API validates (an option id, a member id, YYYY-MM-DD, a
// number, a boolean, {url}), never a display string.

function TextCell({ value, onChange, disabled, multiline }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  useEffect(() => { if (!editing) setDraft(value || ""); }, [value, editing]);
  const commit = () => { setEditing(false); if (draft !== (value || "")) onChange(draft); };
  if (!editing) {
    return (
      <button type="button" disabled={disabled} onClick={() => setEditing(true)}
        className="w-full h-full text-left px-2 py-1.5 text-xs truncate hover:bg-surface-2 disabled:cursor-default">
        {value || <span className="text-fg/25">—</span>}
      </button>
    );
  }
  const Tag = multiline ? "textarea" : "input";
  return (
    <Tag autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter" && !multiline) e.target.blur(); if (e.key === "Escape") { setDraft(value || ""); setEditing(false); } }}
      rows={multiline ? 3 : undefined}
      className="w-full px-2 py-1 text-xs bg-surface border border-brand/50 outline-none" />
  );
}

function StatusCell({ column, value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const opt = optionOf(column, value);
  return (
    <div ref={ref} className="relative h-full">
      <button type="button" disabled={disabled} onClick={() => setOpen((v) => !v)}
        className="w-full h-full px-2 py-1.5 text-xs font-medium text-white flex items-center justify-between gap-1 disabled:cursor-default"
        style={{ background: opt?.color || "#c4c4c4" }} aria-haspopup="listbox" aria-expanded={open}>
        <span className="truncate">{opt?.label || "—"}</span>
        {!disabled && <ChevronDown className="w-3 h-3 opacity-70 shrink-0" />}
      </button>
      {open && (
        <div role="listbox" className="absolute z-20 left-0 top-full mt-1 w-48 bg-surface border border-line/20 shadow-xl p-1">
          {column.options.map((o) => (
            <button key={o.id} type="button" role="option" aria-selected={o.id === value}
              onClick={() => { onChange(o.id); setOpen(false); }}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-surface-2 text-left">
              <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: o.color }} />
              <span className="flex-1 truncate">{o.label}</span>
              {o.id === value && <Check className="w-3 h-3 text-brand" />}
            </button>
          ))}
          <button type="button" role="option" aria-selected={!value} onClick={() => { onChange(null); setOpen(false); }}
            className="w-full px-2 py-1.5 text-xs text-fg/50 hover:bg-surface-2 text-left border-t border-line/10 mt-1">
            Golește
          </button>
        </div>
      )}
    </div>
  );
}

function PersonCell({ value, users, onChange, disabled }) {
  return (
    <select value={value || ""} disabled={disabled} onChange={(e) => onChange(e.target.value || null)}
      className="w-full h-full px-2 py-1.5 text-xs bg-transparent outline-none hover:bg-surface-2 disabled:appearance-none">
      <option value="">—</option>
      {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
    </select>
  );
}

function DateCell({ value, onChange, disabled }) {
  return (
    <input type="date" value={value || ""} disabled={disabled} onChange={(e) => onChange(e.target.value || null)}
      className="w-full h-full px-2 py-1 text-xs bg-transparent outline-none hover:bg-surface-2" />
  );
}

function NumberCell({ value, onChange, disabled }) {
  const [draft, setDraft] = useState(value ?? "");
  useEffect(() => { setDraft(value ?? ""); }, [value]);
  const commit = () => {
    if (draft === "" || draft === null) { if (value !== null && value !== undefined) onChange(null); return; }
    const n = Number(draft);
    if (!Number.isNaN(n) && n !== value) onChange(n);
  };
  return (
    <input type="number" value={draft} disabled={disabled} onChange={(e) => setDraft(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
      className="w-full h-full px-2 py-1 text-xs bg-transparent outline-none hover:bg-surface-2 text-right" />
  );
}

function CheckboxCell({ value, onChange, disabled }) {
  return (
    <label className="w-full h-full flex items-center justify-center cursor-pointer">
      <input type="checkbox" checked={!!value} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="accent-[rgb(var(--brand))]" />
    </label>
  );
}

function LinkCell({ value, onChange, disabled }) {
  const url = typeof value === "object" && value ? value.url : value;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(url || "");
  useEffect(() => { if (!editing) setDraft(url || ""); }, [url, editing]);
  const commit = () => { setEditing(false); const v = draft.trim(); if (v !== (url || "")) onChange(v ? { url: v } : null); };
  if (editing) {
    return <input autoFocus value={draft} placeholder="https://…" onChange={(e) => setDraft(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
      className="w-full px-2 py-1 text-xs bg-surface border border-brand/50 outline-none" />;
  }
  return (
    <div className="flex items-center h-full">
      {url ? (
        <a href={url} target="_blank" rel="noreferrer" className="flex-1 px-2 py-1.5 text-xs text-brand truncate hover:underline flex items-center gap-1">
          <ExternalLink className="w-3 h-3 shrink-0" /><span className="truncate">{(value && value.label) || url.replace(/^https?:\/\//, "")}</span>
        </a>
      ) : <span className="flex-1 px-2 text-xs text-fg/25">—</span>}
      {!disabled && <button type="button" onClick={() => setEditing(true)} className="px-2 text-[10px] text-fg/40 hover:text-fg">editat</button>}
    </div>
  );
}

export default function BoardCell({ column, value, users = [], onChange, disabled = false, multiline = false }) {
  switch (column.type) {
    case "status": return <StatusCell column={column} value={value} onChange={onChange} disabled={disabled} />;
    case "person": return <PersonCell value={value} users={users} onChange={onChange} disabled={disabled} />;
    case "date": return <DateCell value={value} onChange={onChange} disabled={disabled} />;
    case "number": return <NumberCell value={value} onChange={onChange} disabled={disabled} />;
    case "checkbox": return <CheckboxCell value={value} onChange={onChange} disabled={disabled} />;
    case "link": return <LinkCell value={value} onChange={onChange} disabled={disabled} />;
    default: return <TextCell value={value} onChange={onChange} disabled={disabled} multiline={multiline} />;
  }
}
